/**
 * Brand Context
 *
 * Provides brand configuration throughout the verify-ui application.
 * Brand data is extracted from session config responses (returned by portal-api).
 *
 * This context enables:
 * - Dynamic legal links (Terms, Privacy) per brand
 * - Dynamic company name in footer
 * - CSS variable injection for brand colors
 * - Logo URL configuration
 */

import { createContext, useContext, useState, type ReactNode } from 'react';

/**
 * Brand theme colors and assets
 */
export interface BrandTheme {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  logoUrl: string;
  faviconUrl: string;
  primaryHoverColor?: string;
  primaryActiveColor?: string;
  accentColor?: string;
  surfaceSecondary?: string;
  themeMode?: 'light' | 'dark';
}
/**
 * Brand configuration from session config response
 * Matches the brand block structure from portal-api session endpoints
 */
export interface BrandConfig {
  brandSlug?: string;
  productName: string;
  companyName: string;
  theme: BrandTheme;
  logoUrl: string;
  docsUrl?: string;
  supportUrl: string | null;
  privacyUrl: string;
  termsUrl: string;
}

interface BrandContextValue {
  brand: BrandConfig | null;
  setBrand: (brand: BrandConfig) => void;
}

const BrandContext = createContext<BrandContextValue | null>(null);

/**
 * Hook to access brand configuration
 * Returns null if no brand is set - caller must handle this case
 * IMPORTANT: No default brand fallback - brands are fully independent
 */
export function useBrand(): BrandConfig | null {
  const ctx = useContext(BrandContext);
  if (!ctx) {
    return null;
  }
  return ctx.brand;
}

/**
 * Hook to access brand configuration when brand is required
 * Throws if no brand is available
 */
export function useRequiredBrand(): BrandConfig {
  const brand = useBrand();
  if (!brand) {
    throw new Error('Brand is required but not available. Ensure BrandProvider has been initialized with brand data.');
  }
  return brand;
}

/**
 * Hook to access full brand context including setBrand
 * Must be used within BrandProvider
 */
export function useBrandContext(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (!ctx) {
    throw new Error('useBrandContext must be used within BrandProvider');
  }
  return ctx;
}

interface BrandProviderProps {
  children: ReactNode;
  initialBrand?: BrandConfig | null;
}

/**
 * Provider for brand configuration
 * Wraps the app to provide brand context to all components
 * IMPORTANT: No default brand - initialBrand should come from session config
 */
export function BrandProvider({ children, initialBrand = null }: BrandProviderProps) {
  const [brand, setBrand] = useState<BrandConfig | null>(initialBrand);

  return (
    <BrandContext.Provider value={{ brand, setBrand }}>
      {children}
    </BrandContext.Provider>
  );
}
